import type { MouseEvent, PointerEvent } from 'react';
import { useCallback, useRef } from 'react';

type DragState = {
  isDragging: boolean;
  startX: number;
  startY: number;
  scrollLeft: number;
  didDrag: boolean;
  lastDragAt: number;
};

// INFO: % of container width to scroll
const SCROLL_AMOUNT = 0.7;

export const useHorizontalDragScroll = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<DragState>({
    isDragging: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    didDrag: false,
    lastDragAt: 0,
  });

  const setScrollRef = useCallback((node: HTMLDivElement | null) => {
    scrollContainerRef.current = node;
  }, []);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) {
      return;
    }

    dragStateRef.current = {
      isDragging: true,
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: scrollContainer.scrollLeft,
      didDrag: false,
      lastDragAt: dragStateRef.current.lastDragAt,
    };
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const scrollContainer = scrollContainerRef.current;
    const dragState = dragStateRef.current;

    if (!dragState.isDragging || !scrollContainer) {
      return;
    }

    const dragDistanceX = event.clientX - dragState.startX;
    const dragDistanceY = event.clientY - dragState.startY;
    const dragThreshold = event.pointerType === 'touch' ? 8 : 3;

    // Keep native touch scrolling physics on mobile; only mark gesture as drag
    // so we can suppress accidental clicks after swipe.
    if (event.pointerType !== 'mouse') {
      if (Math.abs(dragDistanceX) > dragThreshold || Math.abs(dragDistanceY) > dragThreshold) {
        dragState.didDrag = true;
        dragState.lastDragAt = Date.now();
      }
      return;
    }

    if (Math.abs(dragDistanceX) > dragThreshold || Math.abs(dragDistanceY) > dragThreshold) {
      if (!scrollContainer.hasPointerCapture(event.pointerId)) {
        scrollContainer.setPointerCapture(event.pointerId);
      }
      dragState.didDrag = true;
      dragState.lastDragAt = Date.now();
      event.preventDefault();
    }

    scrollContainer.scrollLeft = dragState.scrollLeft - dragDistanceX;
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer?.hasPointerCapture(event.pointerId)) {
      scrollContainer.releasePointerCapture(event.pointerId);
    }

    dragStateRef.current.isDragging = false;
  };

  const handlePointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer?.hasPointerCapture(event.pointerId)) {
      scrollContainer.releasePointerCapture(event.pointerId);
    }

    dragStateRef.current.isDragging = false;
  };

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;

    if (!dragState.didDrag) {
      return;
    }

    // Block the synthetic click that can follow right after a swipe/drag,
    // but do not block later intentional taps.
    const blockWindowMs = 450;
    const shouldBlock = Date.now() - dragState.lastDragAt <= blockWindowMs;
    dragState.didDrag = false;

    if (shouldBlock) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleDragStart = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleScrollNext = () => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    const scrollAmount = scrollContainer.clientWidth * SCROLL_AMOUNT;
    scrollContainer.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  const handleScrollPrev = () => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    const scrollAmount = scrollContainer.clientWidth * SCROLL_AMOUNT;
    scrollContainer.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  };

  return {
    setScrollRef,
    scrollContainerRef,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    handleClickCapture,
    handleDragStart,
    handleScrollNext,
    handleScrollPrev,
  };
};
